import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, ShoppingCart, ChevronDown, ChevronRight, FileText, Package, FlaskConical, Receipt, Building2 } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import { formatArgentinaDate } from '../../utils/dateUtils';
import { Supplier } from '../../types';

interface PurchasesDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchasesData: {
    totalPurchases: number;
    supplierInvoicesTotal: number;
    expensesTotal: number;
    productionTaskCosts: number;
    sampleTaskCosts: number;
  };
  period: string;
  periodSupplierInvoices: any[];
  periodExpenses: any[];
  suppliers: Supplier[];
  periodProductionTasks?: any[];
  periodSampleTasks?: any[];
  productions?: any[];
  samples?: any[];
}

const PurchasesDetailModal: React.FC<PurchasesDetailModalProps> = ({
  isOpen,
  onClose,
  purchasesData,
  period,
  periodSupplierInvoices,
  periodExpenses,
  suppliers,
  periodProductionTasks = [],
  periodSampleTasks = [],
  productions = [],
  samples = [],
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    productionTasks: true,
    sampleTasks: true,
    supplierInvoices: false,
    expenses: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getSupplierName = (supplierId: string) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier?.name || 'Proveedor desconocido';
  };

  const getProductionName = (productionId: string) => {
    const production = productions.find((p: any) => p.id === productionId);
    return production?.name || `Producción #${productionId?.slice(0, 8)}`;
  };

  const getSampleName = (sampleId: string) => {
    const sample = samples.find((s: any) => s.id === sampleId);
    return sample?.name || `Muestra #${sampleId?.slice(0, 8)}`;
  };

  const getPeriodLabel = () => {
    switch (period) {
      case 'month': return 'este mes';
      case 'month-1': return 'mes anterior';
      case 'month-2': return 'hace 2 meses';
      case 'month-3': return 'hace 3 meses';
      case 'month-4': return 'hace 4 meses';
      case 'quarter': return 'este trimestre';
      case 'year': return 'este año';
      case 'custom': return 'período personalizado';
      default: return period || 'el período';
    }
  };

  // Calcular porcentajes
  const getPercentage = (value: number) => {
    if (purchasesData.totalPurchases === 0) return 0;
    return Math.round((value / purchasesData.totalPurchases) * 100);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-[100] max-h-[90vh] w-[95vw] max-w-[900px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg overflow-y-auto">
          <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
            <ShoppingCart className="mr-2 h-5 w-5 text-orange-600 dark:text-orange-400" />
            Detalle de Compras del Período ({getPeriodLabel()})
          </Dialog.Title>
          
          <div className="space-y-6">
            {/* Resumen Total */}
            <div className="bg-orange-50 dark:bg-orange-900/30 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-orange-700 dark:text-orange-300">Total Compras</p>
                  <p className="text-2xl font-bold text-orange-800 dark:text-orange-200">{formatCurrency(purchasesData.totalPurchases)}</p>
                </div>
                <div className="p-3 bg-orange-100 dark:bg-orange-800 rounded-full">
                  <ShoppingCart className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
            </div>

            {/* Resumen por categoría */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                <p className="text-xs font-medium text-purple-700 dark:text-purple-300">Tareas Producción</p>
                <p className="text-lg font-bold text-purple-800 dark:text-purple-200">{formatCurrency(purchasesData.productionTaskCosts)}</p>
                <p className="text-xs text-purple-600 dark:text-purple-400">{getPercentage(purchasesData.productionTaskCosts)}%</p>
              </div>
              <div className="bg-pink-50 dark:bg-pink-900/20 p-3 rounded-lg">
                <p className="text-xs font-medium text-pink-700 dark:text-pink-300">Tareas Muestras</p>
                <p className="text-lg font-bold text-pink-800 dark:text-pink-200">{formatCurrency(purchasesData.sampleTaskCosts)}</p>
                <p className="text-xs text-pink-600 dark:text-pink-400">{getPercentage(purchasesData.sampleTaskCosts)}%</p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                <p className="text-xs font-medium text-blue-700 dark:text-blue-300">Fact. Proveedores</p>
                <p className="text-lg font-bold text-blue-800 dark:text-blue-200">{formatCurrency(purchasesData.supplierInvoicesTotal)}</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">{getPercentage(purchasesData.supplierInvoicesTotal)}%</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Gastos</p>
                <p className="text-lg font-bold text-gray-800 dark:text-gray-200">{formatCurrency(purchasesData.expensesTotal)}</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">{getPercentage(purchasesData.expensesTotal)}%</p>
              </div>
            </div>

            {/* Tareas de Producción */}
            <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => toggleSection('productionTasks')}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center">
                  <Package className="mr-2 h-5 w-5 text-purple-600 dark:text-purple-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Tareas de Producción ({periodProductionTasks.length})
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                    {formatCurrency(purchasesData.productionTaskCosts)}
                  </span>
                  {expandedSections.productionTasks ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                </div>
              </button>
              
              {expandedSections.productionTasks && periodProductionTasks.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-600 p-4 bg-gray-50 dark:bg-gray-600 max-h-60 overflow-y-auto">
                  <div className="space-y-2">
                    {periodProductionTasks
                      .filter(task => (task.cost || 0) > 0)
                      .sort((a, b) => (b.cost || 0) - (a.cost || 0))
                      .map((task) => (
                        <div key={task.id} className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{task.name || 'Tarea'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {getProductionName(task.production_id)} • {getSupplierName(task.supplier_id)}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              {formatArgentinaDate((() => {
                                const prod = productions.find((p: any) => p.id === task.production_id);
                                const d = (prod as { start_date?: string })?.start_date ?? prod?.created_at?.slice(0, 10) ?? task.created_at;
                                return d;
                              })())}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-purple-600 dark:text-purple-400">{formatCurrency(task.cost || 0)}</p>
                          </div>
                        </div>
                      ))}
                    {periodProductionTasks.filter(t => (t.cost || 0) > 0).length === 0 && (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-2">No hay tareas con costo</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Tareas de Muestras */}
            <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => toggleSection('sampleTasks')}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center">
                  <FlaskConical className="mr-2 h-5 w-5 text-pink-600 dark:text-pink-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Tareas de Muestras ({periodSampleTasks.length})
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-pink-600 dark:text-pink-400">
                    {formatCurrency(purchasesData.sampleTaskCosts)}
                  </span>
                  {expandedSections.sampleTasks ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                </div>
              </button>
              
              {expandedSections.sampleTasks && periodSampleTasks.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-600 p-4 bg-gray-50 dark:bg-gray-600 max-h-60 overflow-y-auto">
                  <div className="space-y-2">
                    {periodSampleTasks
                      .filter(task => (task.cost || 0) > 0)
                      .sort((a, b) => (b.cost || 0) - (a.cost || 0))
                      .map((task) => (
                        <div key={task.id} className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{task.name || 'Tarea'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {getSampleName(task.sample_id)} • {getSupplierName(task.supplier_id)}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              {formatArgentinaDate((() => {
                                const smpl = samples.find((s: any) => s.id === task.sample_id);
                                const d = (smpl as { start_date?: string })?.start_date ?? smpl?.created_at?.slice(0, 10) ?? task.created_at;
                                return d;
                              })())}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-pink-600 dark:text-pink-400">{formatCurrency(task.cost || 0)}</p>
                          </div>
                        </div>
                      ))}
                    {periodSampleTasks.filter(t => (t.cost || 0) > 0).length === 0 && (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-2">No hay tareas con costo</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Facturas de Proveedores */}
            <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => toggleSection('supplierInvoices')}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center">
                  <FileText className="mr-2 h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Facturas de Proveedores ({periodSupplierInvoices.length})
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                    {formatCurrency(purchasesData.supplierInvoicesTotal)}
                  </span>
                  {expandedSections.supplierInvoices ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                </div>
              </button>
              
              {expandedSections.supplierInvoices && periodSupplierInvoices.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-600 p-4 bg-gray-50 dark:bg-gray-600 max-h-60 overflow-y-auto">
                  <div className="space-y-2">
                    {periodSupplierInvoices
                      .sort((a, b) => (b.total || 0) - (a.total || 0))
                      .map((invoice) => (
                        <div key={invoice.id} className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">Factura {invoice.number}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              <Building2 className="inline h-3 w-3 mr-1" />
                              {getSupplierName(invoice.entity_id)}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">{formatArgentinaDate(invoice.date)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">{formatCurrency(invoice.total || 0)}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Gastos */}
            <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => toggleSection('expenses')}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center">
                  <Receipt className="mr-2 h-5 w-5 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Gastos ({periodExpenses.length})
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                    {formatCurrency(purchasesData.expensesTotal)}
                  </span>
                  {expandedSections.expenses ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                </div>
              </button>
              
              {expandedSections.expenses && periodExpenses.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-600 p-4 bg-gray-50 dark:bg-gray-600 max-h-60 overflow-y-auto">
                  <div className="space-y-2">
                    {periodExpenses
                      .sort((a, b) => (b.amount || 0) - (a.amount || 0))
                      .map((expense) => (
                        <div key={expense.id} className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{expense.description || 'Gasto'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{expense.category || 'Sin categoría'}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">{formatArgentinaDate(expense.date)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">{formatCurrency(expense.amount || 0)}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Si no hay datos */}
            {purchasesData.totalPurchases === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium">No hay compras en este período</p>
              </div>
            )}
          </div>

          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default PurchasesDetailModal;
